import { Injectable, Injector } from '@angular/core';
import { merge } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { Point2D } from '../../../team-apis/otterdam/answers/shape-typings';
import { InspectService } from '../toolbars/inspect.service';
import { ShapeTranslationHelper } from '../tools/edit/shape-translation-helper';
import { AbstractTranslationService } from './abstract-translation-service';
import { ViewBoxService } from './view-box.service';

@Injectable()
export class ShapeTranslationService extends AbstractTranslationService {
  protected method = 'translate';

  constructor(
    private inspectService: InspectService,
    private viewBoxService: ViewBoxService,
    injector: Injector,
  ) {
    super(injector);
  }

  override setUpKeyboardShortcuts() {
    super.setUpKeyboardShortcuts(
      this.createTranslationHelper$(this.getOffsets$()).pipe(
        tap((translationHelper) => {
          if (translationHelper.hasOutOfViewShape()) {
            const candidateShapeStores =
              translationHelper.getCandidateShapeStores();

            if (candidateShapeStores.length === 1) {
              void this.inspectService.inspectShape(candidateShapeStores[0]);
            } else {
              this.viewBoxService.resetZoom();
            }
          }
        }),
      ),
    );
  }

  protected getTranslationHelper(_offset: Point2D) {
    return new ShapeTranslationHelper(this.injector);
  }

  private getOffsets$() {
    // Matching the nudging used in 3D for smaller pixel precisions.
    const getNudgeDelta = () => (this.store.state().pixelSize > 0.01 ? 5 : 0.1);
    const getSmallNudgeDelta = () =>
      this.store.state().pixelSize > 0.01 ? 1 : 0.01;
    const fastOffset$ = this.createOffset$(
      {
        leftShortcutName: 'nudgeLeft',
        rightShortcutName: 'nudgeRight',
        downShortcutName: 'nudgeDown',
        upShortcutName: 'nudgeUp',
      },
      getNudgeDelta,
    ).pipe(map((delta) => ({ delta })));
    const slowOffset$ = this.createOffset$(
      {
        leftShortcutName: 'nudgeLeftSlow',
        rightShortcutName: 'nudgeRightSlow',
        downShortcutName: 'nudgeDownSlow',
        upShortcutName: 'nudgeUpSlow',
      },
      getSmallNudgeDelta,
    ).pipe(map((delta) => ({ delta, isSmallNudge: true })));
    return merge(fastOffset$, slowOffset$);
  }
}
